// src/pages/Agent.js
//
// The full-page Decode Agent surface (frontend spec §8.3): resolves the
// active chat id from the route, owns layout state (sidebar collapse, file
// pane) and the canvas-edit state cluster (transplanted intact from
// AssistantSidebar), and composes the regions.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Snackbar, Alert, useMediaQuery,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { getSignedUrl } from '../services/api_utils';
import { executeResourceDelete } from '../services/resourceActions';
import OpAccumulator from '../utils/opAccumulator';
import canvasEditGuard from '../utils/canvasEditGuard';
import { exportWorkbookBlob, downloadBlob } from '../utils/canvasWorkbook';
import useAgentSession from '../components/agent/session/useAgentSession';
import {
  agentFilePartForToolUse,
  newestWorkingFileVersion,
  workingFileChangedElsewhere,
} from '../components/agent/session/partModel';
import useChatList from '../components/agent/session/useChatList';
import ChatSidebar from '../components/agent/ChatSidebar';
import ChatTopBar from '../components/agent/ChatTopBar';
import ChatThread from '../components/agent/ChatThread';
import Composer, { MAX_TOTAL_UPLOAD_BYTES } from '../components/agent/Composer';
import FilePane from '../components/agent/FilePane';
import LoadFailure from '../components/common/LoadFailure';

// The export is a chunk download (the xlsx writer, loaded on demand) plus an
// in-memory transform of the sheet. The first can fail transiently, so it is
// tried three times; the second fails the same way every time, and the last
// error is what the caller sees.
const EXPORT_ATTEMPTS = 3;
const EXPORT_RETRY_DELAY_MS = 400;
async function exportWorkbookWithRetry(workbookRef) {
  let lastError = null;
  for (let attempt = 1; attempt <= EXPORT_ATTEMPTS; attempt += 1) {
    try {
      return await exportWorkbookBlob(workbookRef);
    } catch (error) {
      lastError = error;
      if (attempt < EXPORT_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, EXPORT_RETRY_DELAY_MS));
      }
    }
  }
  throw lastError;
}

const EXT_CONTENT_TYPES = {
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.log': 'text/plain',
};

const contentTypeFor = (filename) => {
  const extension = '.' + (filename || '').split('.').pop().toLowerCase();
  return EXT_CONTENT_TYPES[extension] || 'application/octet-stream';
};

// What leaving would lose, for the discard dialog and the back-button confirm.
const CANVAS_EDITS_WARNING = "Your canvas edits haven't been sent to the agent and will be lost.";
const QUEUED_SEND_WARNING = 'A message is still waiting to be sent to this chat and will be discarded.';
// Why the pane cannot be closed, or another file opened in it, for the moment.
const CANVAS_EDITS_BEING_SENT = 'Your canvas edits are still being sent to the Decode Agent.';
const unsentWorkWarning = (canvasHasEdits, queuedSend) => [
  canvasHasEdits ? CANVAS_EDITS_WARNING : null,
  queuedSend ? QUEUED_SEND_WARNING : null,
].filter(Boolean).join(' ');

function Agent() {
  const { companyName } = useAuth();
  const chatList = useChatList(companyName);
  const { forgetChat, patchChatName } = chatList;
  const { agentId: routeAssistantId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  // ── Chat identity (§8.3): route param ?? draft ref. sendTurn mints the
  // draft id into the ref BEFORE POSTing; its optimistic setState forces the
  // render that carries the new effective id into the tree (mint-then-set).
  const draftIdRef = useRef(null);
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const effectiveAssistantId = routeAssistantId ?? draftIdRef.current ?? null;
  // For work that resolves after a chat switch (a delete request) and must
  // act on the chat shown then, not the one shown when it started.
  const effectiveAssistantIdRef = useRef(effectiveAssistantId);
  effectiveAssistantIdRef.current = effectiveAssistantId;

  // Once the route param carries the id (post-stream_start navigate), the
  // draft ref has done its job.
  useEffect(() => {
    if (routeAssistantId != null) draftIdRef.current = null;
  }, [routeAssistantId]);

  // ── Layout state.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('agent.sidebar_collapsed') === 'true'
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [filePaneCollapsed, setFilePaneCollapsed] = useState(false);

  // ── Snackbar (no global system — a local notify helper, §practical notes).
  const [snackbar, setSnackbar] = useState(null);
  // `retryable`: Retry re-sends the last chat message. `onRetry`: Retry runs
  // that action instead — an approval decision retries itself, not the send.
  const notify = useCallback((message, options = {}) => {
    setSnackbar({
      message,
      severity: options.severity || 'info',
      retryable: !!options.retryable,
      onRetry: typeof options.onRetry === 'function' ? options.onRetry : null,
    });
  }, []);

  // ── Canvas-edit state cluster — verbatim transplant from AssistantSidebar.
  const [canvasFile, setCanvasFile] = useState(null);
  // Unsent edits, counted by the accumulator — the one source the header chip, the
  // composer chip, the leave guards and the send payload all read. The importer's
  // own normalisation ops (empty row-height / column-width maps it adds a moment
  // after loading) are ignored by the accumulator, so they never count.
  const [canvasEditCount, setCanvasEditCount] = useState(0);
  const canvasHasEdits = canvasEditCount > 0;
  // Whether the live workbook has ever diverged from the bytes the pane fetched.
  // Unlike the unsent count this survives a successful send: the pane still shows
  // the edited workbook, so Download must keep exporting it rather than the
  // original bytes.
  const [canvasEverEdited, setCanvasEverEdited] = useState(false);
  const [discardPrompt, setDiscardPrompt] = useState(null);
  // Download & Discard is exporting the workbook; the prompt's buttons wait.
  const [downloadingEdits, setDownloadingEdits] = useState(false);
  // A send is being prepared or carried out. The workbook is read-only for the
  // whole of it: the export snapshots the sheets, and an edit made after that
  // snapshot but before the turn's own lock took over would be marked sent
  // without ever having been uploaded (X13-11).
  const [sendInProgress, setSendInProgress] = useState(false);
  // A send whose canvas edits would not export: { text, files, filename, count }.
  // Held here until the user chooses to send without them or to cancel.
  const [exportFailurePrompt, setExportFailurePrompt] = useState(null);
  // A send whose canvas edits were made on a workbook that has since changed
  // somewhere else: { text, files, filename }. Sending would overwrite that
  // change, so the message waits here for the user to decide (bug 13.1).
  const [staleCanvasPrompt, setStaleCanvasPrompt] = useState(null);
  const opAccumulatorRef = useRef(new OpAccumulator());
  const workbookRef = useRef(null);
  const canvasOpenRequestRef = useRef(0);
  // Every canvas push this page has opened, from the live stream or from the
  // ledger. A push is opened once: the ledger's view of a turn can still end at
  // a workbook the stream already replaced, and reopening that would walk the
  // pane backwards onto an older version of the file.
  const openedCanvasPushesRef = useRef(new Set());
  const composerRef = useRef(null);
  const centreColumnRef = useRef(null);
  // The session hook's latest return value, for callbacks that outlive a render.
  // Declared here, ahead of the hook, because a callback handed to the hook
  // reads it too.
  const sessionRef = useRef(null);

  const clearCanvasState = useCallback(() => {
    canvasOpenRequestRef.current += 1;
    setCanvasFile(null);
    opAccumulatorRef.current.reset();
    setCanvasEditCount(0);
    setCanvasEverEdited(false);
  }, []);

  // ── Session wiring.
  // A draft's id is minted here and never checked against existing chats: the
  // backend creates the document on the first send and would append to an
  // existing chat if the id collided. Sixteen hex characters (64 bits) keep a
  // collision out of reach; eight left it at a birthday-problem chance of about
  // one in a hundred once a company had ten thousand chats (B9-10).
  const ensureAssistantId = useCallback(() => {
    if (routeAssistantId) return { id: routeAssistantId, isNew: false };
    if (!draftIdRef.current) draftIdRef.current = crypto.randomUUID().replace(/-/g, '').slice(0, 16);
    return { id: draftIdRef.current, isNew: true };
  }, [routeAssistantId]);

  const handleChatCreated = useCallback((id, autoName) => {
    // §7.1 step 6: the skeleton already exists (claim ran before any stream
    // bytes), so the auto-name merge write cannot be clobbered.
    navigate(`/agent/${id}`, { replace: true });
    if (companyName) {
      setDoc(doc(db, 'users', companyName, 'agents', id), { chat_name: autoName }, { merge: true })
        .catch((error) => console.error('Error naming new chat:', error));
    }
  }, [navigate, companyName]);

  // Live canvas_file event — reset edit tracking exactly as v1's handler,
  // and expand the pane if collapsed.
  const handleCanvasFileEvent = useCallback((file) => {
    // The ledger may have delivered this push first, in which case the pane is
    // already showing it or is fetching it.
    if (file.toolUseId && openedCanvasPushesRef.current.has(file.toolUseId)) return;
    canvasOpenRequestRef.current += 1;
    if (file.toolUseId) openedCanvasPushesRef.current.add(file.toolUseId);
    opAccumulatorRef.current.reset();
    setCanvasEditCount(0);
    setCanvasEverEdited(false);
    setCanvasFile(file);
    setFilePaneCollapsed(false);
  }, []);

  // After a successful send that carried canvas edits, re-snapshot so
  // subsequent edits track from the sent state (v1 behaviour). The workbook
  // stays diverged from the fetched bytes (canvasEverEdited), so Download keeps
  // exporting the live editor.
  const handleSendComplete = useCallback(({ hadCanvasEdit }) => {
    if (!hadCanvasEdit) return;
    if (workbookRef.current) {
      const currentSheets = workbookRef.current.getAllSheets();
      opAccumulatorRef.current.setOriginalSnapshot(currentSheets);
    }
    setCanvasEditCount(0);
  }, []);

  const restoreComposer = useCallback(({ text, files }) => {
    composerRef.current?.restore({ text, files });
  }, []);

  // The chat this page is deleting, while its request is in flight. The
  // session listener observes the deletion flag before the response arrives.
  // That is this page's doing and is not reported as a deletion elsewhere.
  const deletingChatIdRef = useRef(null);

  // The chat whose parked message the person has just been told they are
  // about to lose, so the chat-change effect does not replace that explanation
  // with its own notice when that chat is left. The page shows one notice at a
  // time. Naming the chat, rather than keeping a flag, means an explanation
  // can never be applied to a different chat's message.
  const parkedMessageLossExplainedForRef = useRef(null);

  const handleChatDeletedElsewhere = useCallback((id, { deleting = false } = {}) => {
    if (!deleting) forgetChat(id);
    // "This chat was deleted" below is the explanation; a second notice about
    // the parked message would replace it.
    parkedMessageLossExplainedForRef.current = id;
    navigate('/agent');
    if (deletingChatIdRef.current === id) return;
    notify(deleting ? 'This chat is being deleted' : 'This chat was deleted', { severity: 'info' });
  }, [navigate, notify, forgetChat]);

  // A queued message was built when Send was pressed, which can be three minutes
  // before it fires. Its canvas edits are checked again at that moment, against
  // the version they were made on (kept with the payload, because the canvas
  // itself may have moved on since). A stale one goes to the prompt, which
  // sends this same payload if the user chooses to send anyway.
  const holdQueuedSend = useCallback((payload) => {
    // Nothing to check, or the user has already chosen to send anyway.
    if (!payload.canvasEdit || payload.staleOverwriteConfirmed) return false;
    const changedElsewhere = workingFileChangedElsewhere(
      sessionRef.current.messages,
      payload.canvasEdit.openedFrom,
      sessionRef.current.isOwnSend,
    );
    if (!changedElsewhere) return false;
    setStaleCanvasPrompt({
      text: payload.text,
      files: payload.files,
      filename: payload.canvasEdit.filename,
      queuedPayload: payload,
    });
    return true;
  }, []);

  const session = useAgentSession({
    companyName,
    agentId: effectiveAssistantId,
    isDraft: routeAssistantId == null,
    sessionEpoch,
    ensureAssistantId,
    onChatCreated: handleChatCreated,
    onCanvasFile: handleCanvasFileEvent,
    onSendComplete: handleSendComplete,
    restoreComposer,
    holdQueuedSend,
    onChatDeleted: handleChatDeletedElsewhere,
    notify,
  });
  sessionRef.current = session;

  // ── Leave guards. Two kinds of unsent work: canvas edits, and a message that
  // has left the composer but has not been sent. Such a message is either
  // queued behind a busy turn (§7.6) or parked in one of the two send prompts
  // (stale workbook, edits that would not export); the Composer cleared itself
  // when Send was pressed, so the prompt is then the only copy. A chat switch
  // would otherwise drop it silently. Canvas operations (opening another file,
  // closing the pane) risk only the edits; leaving the chat risks both.
  const queuedSendPending = session.queuedSend || !!staleCanvasPrompt || !!exportFailurePrompt;
  const hasUnsentWork = canvasHasEdits || queuedSendPending;

  // Canvas edits that are on their way to the agent: a send is exporting them,
  // or a queued or sent message already holds their export. They stay counted
  // as unsent until the sent message is seen saved, and until then the discard
  // prompt's warning, that they "haven't been sent and will be lost", would be
  // false. While the export itself is running there is a second reason:
  // closing the pane would unmount the workbook being exported. So replacing
  // or closing the workbook waits for the send.
  const canvasEditsBeingSent = canvasHasEdits && (sendInProgress || session.canvasEditInFlight);

  const requestDiscardEdits = useCallback((proceed) => {
    if (canvasEditsBeingSent) {
      notify(`${CANVAS_EDITS_BEING_SENT} Try again in a moment.`, { severity: 'info' });
      return;
    }
    if (!canvasHasEdits) {
      proceed();
      return;
    }
    setDiscardPrompt({ proceed, queuedSend: false });
  }, [canvasEditsBeingSent, canvasHasEdits, notify]);

  const requestLeaveChat = useCallback((proceed) => {
    if (!canvasHasEdits && !queuedSendPending) {
      proceed();
      return;
    }
    setDiscardPrompt({ proceed, queuedSend: queuedSendPending });
  }, [canvasHasEdits, queuedSendPending]);

  const historyBlocker = useBlocker(
    useCallback(
      ({ historyAction }) => historyAction === 'POP' && hasUnsentWork,
      [hasUnsentWork]
    )
  );

  useEffect(() => {
    if (historyBlocker.state !== 'blocked') return;
    const message = `${unsentWorkWarning(canvasHasEdits, queuedSendPending)} Do you want to leave this page?`;
    if (window.confirm(message)) {
      // The confirm just said the waiting message will be discarded. The chat
      // is read from its ref so that this effect, which opens a blocking
      // dialog, gains no new reason to run again.
      if (queuedSendPending) parkedMessageLossExplainedForRef.current = effectiveAssistantIdRef.current;
      setTimeout(historyBlocker.proceed, 0);
    } else {
      historyBlocker.reset();
    }
  }, [historyBlocker, canvasHasEdits, queuedSendPending]);

  // Header/page nav links consult this before navigating.
  useEffect(() => {
    canvasEditGuard.isDirty = () => hasUnsentWork;
    canvasEditGuard.request = (proceed) => setDiscardPrompt({ proceed, queuedSend: queuedSendPending });
    return () => {
      canvasEditGuard.isDirty = () => false;
      canvasEditGuard.request = null;
    };
  }, [hasUnsentWork, queuedSendPending]);

  useEffect(() => {
    if (!hasUnsentWork) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsentWork]);

  // ── Canvas clears on effective-id change: non-null → different non-null,
  // or → null. The null → non-null draft transition keeps the canvas (its
  // edits are the payload being sent).
  const prevEffectiveIdRef = useRef(effectiveAssistantId);
  useEffect(() => {
    const prev = prevEffectiveIdRef.current;
    prevEffectiveIdRef.current = effectiveAssistantId;
    if (prev === effectiveAssistantId || prev == null) return;
    const lossAlreadyExplained = parkedMessageLossExplainedForRef.current === prev;
    parkedMessageLossExplainedForRef.current = null;
    // Which pushes have been opened is per chat: coming back to one should show
    // its last turn's workbook again, exactly as arriving at it the first time
    // does. Closing the pane deliberately is not a chat change and must not
    // reset this, or the next snapshot would pull the pane straight back open.
    openedCanvasPushesRef.current = new Set();
    clearCanvasState();
    // A message parked in a send prompt belongs to the chat that was left. Left
    // open, the prompt's send button would post that chat's text, files and
    // workbook into the chat now on screen.
    if (staleCanvasPrompt || exportFailurePrompt) {
      setStaleCanvasPrompt(null);
      setExportFailurePrompt(null);
      if (!lossAlreadyExplained) {
        notify('A message that was waiting to be sent to the previous chat was discarded.', { severity: 'warning' });
      }
    }
  }, [effectiveAssistantId, clearCanvasState, staleCanvasPrompt, exportFailurePrompt, notify]);

  // ── Canvas open handlers (transplanted from AssistantSidebar).
  // Resolves to true when the file was opened, false when it could not be
  // opened, and null when a newer open request (or a chat change) overtook
  // this one, which is not a failure: the canvas already shows something newer.
  const openInCanvas = useCallback(async ({
    filename, downloadUrl, gcsPath, sandboxPath, toolUseId, contentType, description = '',
  }) => {
    const requestId = ++canvasOpenRequestRef.current;
    try {
      // An agent file is a working file the canvas can edit, so the canvas
      // opens that file's newest saved version: a card in the thread may be an
      // older one, from before a user's canvas edit or a later copy from the
      // agent. Edits made on an older version would silently undo everything
      // since, because a canvas edit replaces the whole working file (bug
      // 13.1). Only a file the agent produced (it has a tool_use_id) is a
      // working file; a user's upload or an image opens exactly as clicked.
      //
      // A card that was still streaming when it was clicked carries neither
      // path; both are taken from the agent's file part once the ledger has it.
      const messages = sessionRef.current.messages;
      const openedFromPart = agentFilePartForToolUse(messages, toolUseId);
      const cardGcsPath = gcsPath || openedFromPart?.gcsPath || null;
      const cardSandboxPath = sandboxPath || openedFromPart?.sandboxPath || null;
      const newestVersion = toolUseId ? newestWorkingFileVersion(messages, cardSandboxPath) : null;
      const versionGcsPath = newestVersion ? newestVersion.gcsPath : cardGcsPath;
      const versionToolUseId = newestVersion ? newestVersion.toolUseId : toolUseId;
      const versionFilename = newestVersion?.filename || filename;
      const openedNewerVersion = !!newestVersion && newestVersion.gcsPath !== cardGcsPath;

      // For historical cards the SSE download URL has likely expired —
      // always re-sign from the storage path. Resolve before touching canvas
      // state so a failed open leaves the current file intact.
      const url = versionGcsPath ? await getSignedUrl(versionGcsPath) : downloadUrl;
      if (canvasOpenRequestRef.current !== requestId) return null;
      if (!url) return false;

      opAccumulatorRef.current.reset();
      setCanvasEditCount(0);
      setCanvasEverEdited(false);
      setCanvasFile({
        url,
        // The version this workbook was opened from: the baseline a later
        // send is checked against.
        gcsPath: versionGcsPath || null,
        // The name of the version actually shown, which a later canvas edit
        // sends back as its source file.
        filename: versionFilename,
        contentType,
        description,
        toolUseId: versionToolUseId || null,
        sandboxPath: cardSandboxPath,
      });
      setFilePaneCollapsed(false);
      if (openedNewerVersion) {
        notify(`Opened the latest version of ${versionFilename}, which includes changes made after this copy was sent.`, {
          severity: 'info',
        });
      }
      return true;
    } catch (error) {
      if (canvasOpenRequestRef.current !== requestId) return null;
      console.error('Error opening file:', error);
      notify('Failed to open the file.', { severity: 'error' });
      return false;
    }
  }, [notify]);

  // The canvas shows the workbook the agent's last turn pushed to it, read from
  // the ledger rather than from the stream that announced it. A browser that
  // lost the connection mid-turn would otherwise keep showing the version it
  // submitted, and the next edit it sent would be made against that stale copy,
  // which the backend stages over the agent's newer work (X13-10).
  useEffect(() => {
    const pushes = session.canvasPushes;
    const push = pushes.length > 0 ? pushes[pushes.length - 1] : null;
    if (!push || openedCanvasPushesRef.current.has(push.toolUseId)) return;
    openedCanvasPushesRef.current.add(push.toolUseId);
    // Read from the accumulator, not the render's edit count: an acknowledgement
    // in this same commit has already cleared the accumulator while the count
    // still shows the old value, and warning then would claim edits were lost
    // that are safely in the sent message.
    const replaced = opAccumulatorRef.current.hasEdits() ? canvasFile?.filename : null;
    openInCanvas({
      filename: push.filename,
      description: push.description,
      gcsPath: push.gcsPath,
      sandboxPath: push.sandboxPath,
      toolUseId: push.toolUseId,
      contentType: contentTypeFor(push.filename),
    }).then((opened) => {
      if (!opened) {
        // Nothing was replaced, so let a later snapshot try this push again.
        openedCanvasPushesRef.current.delete(push.toolUseId);
        return;
      }
      // The agent's version wins, exactly as it does for a live canvas_file
      // event, but say so rather than dropping the edits quietly.
      if (replaced) {
        notify(`The Decode Agent replaced ${replaced} in the canvas, discarding your unsent edits to it.`, {
          severity: 'warning',
        });
      }
    });
  }, [session.canvasPushes, canvasFile, notify, openInCanvas]);

  // Widened beyond v1's xlsx/docx gate: every FilePane-supported type opens
  // (unknown types get the in-pane download card, §8.9).
  const handleShowInCanvas = useCallback(({ filename, downloadUrl, gcsPath, sandboxPath, toolUseId }) => {
    requestDiscardEdits(() => openInCanvas({
      filename, downloadUrl, gcsPath, sandboxPath, toolUseId, contentType: contentTypeFor(filename),
    }));
  }, [requestDiscardEdits, openInCanvas]);

  const handleCanvasOp = useCallback((ops) => {
    const accumulator = opAccumulatorRef.current;
    accumulator.ingest(ops);
    const count = accumulator.editCount();
    setCanvasEditCount(count);
    if (count > 0) setCanvasEverEdited(true);
  }, []);

  const handleCanvasLoad = useCallback((sheetData) => {
    opAccumulatorRef.current.setOriginalSnapshot(sheetData);
  }, []);

  const handleDiscardEdits = () => {
    const { proceed } = discardPrompt;
    setDiscardPrompt(null);
    proceed();
  };

  // The export can fail transiently, so it is retried; a failure keeps the
  // prompt and the edits in place rather than discarding what could not be
  // saved (B9-5).
  const handleDownloadAndDiscardEdits = async () => {
    setDownloadingEdits(true);
    try {
      const blob = await exportWorkbookWithRetry(workbookRef);
      downloadBlob(blob, canvasFile?.filename || 'canvas_edit.xlsx');
    } catch (error) {
      console.error('Error exporting canvas edits for download:', error);
      notify('Could not export your edits for download. Nothing was discarded.', { severity: 'error' });
      return;
    } finally {
      setDownloadingEdits(false);
    }
    handleDiscardEdits();
  };

  // ── Navigation handlers (guarded by unsent canvas edits and queued sends).
  const handleSelectChat = useCallback((id) => {
    setMobileSidebarOpen(false);
    if (id === effectiveAssistantId) return;
    requestLeaveChat(() => navigate(`/agent/${id}`));
  }, [effectiveAssistantId, requestLeaveChat, navigate]);

  const handleNewChat = useCallback(() => {
    setMobileSidebarOpen(false);
    requestLeaveChat(() => {
      // "New chat" at /agent changes no route param — the explicit session
      // epoch bump (plus the cleared ref) resets the session (§8.3).
      draftIdRef.current = null;
      setSessionEpoch((epoch) => epoch + 1);
      clearCanvasState();
      if (routeAssistantId) navigate('/agent');
    });
  }, [requestLeaveChat, clearCanvasState, routeAssistantId, navigate]);

  // Delete through the backend cascade, which removes the Firestore subtree
  // (messages, jobs, approvals, subagents) and the chat's storage artifacts.
  // The page leaves an open chat only once the backend has confirmed the
  // delete, so a refused or failed delete leaves the user where they were
  // (B9-8). Throws when nothing was deleted, so the sidebar keeps the row.
  const handleDeleteChat = useCallback(async (id) => {
    deletingChatIdRef.current = id;
    try {
      await executeResourceDelete('agent', id, null);
    } catch (error) {
      deletingChatIdRef.current = null;
      if (error.blocked) {
        notify(error.message === 'blocked' ? 'The agent is currently working on this chat.' : error.message, { severity: 'info' });
        throw error;
      }
      if (error.status === 404) {
        // Already deleted elsewhere: the row can go, and if it was the open
        // chat its own listener has moved the page on.
        return;
      }
      console.error('Error deleting chat:', error);
      notify('Chat deletion was interrupted. Retry deletion from the chat menu.', { severity: 'error' });
      throw error;
    }
    deletingChatIdRef.current = null;
    if (id === effectiveAssistantIdRef.current) navigate('/agent');
    notify('Chat deleted', { severity: 'info' });
  }, [navigate, notify]);

  // A rename made from the top bar. A sidebar row for a chat that was paged in
  // from older history is a static copy that no listener updates, so it is
  // given the new name here, exactly as a rename from the row's own menu does.
  // A failed write throws before the row is touched, and the rename dialog
  // shows that failure.
  const handleRenameChat = useCallback(async (newName) => {
    // The write resolves later, and the person may have opened another chat by
    // then, so the chat being renamed is fixed now.
    const renamedChatId = effectiveAssistantId;
    await sessionRef.current.renameChat(newName);
    patchChatName(renamedChatId, newName);
  }, [effectiveAssistantId, patchChatName]);

  const handleSidebarCollapsedChange = useCallback((next) => {
    localStorage.setItem('agent.sidebar_collapsed', String(next));
    setSidebarCollapsed(next);
  }, []);

  const handleOpenSidebar = useCallback(() => {
    if (isMobile) {
      setMobileSidebarOpen(true);
    } else {
      handleSidebarCollapsedChange(false);
    }
  }, [isMobile, handleSidebarCollapsedChange]);

  // ── Send wiring: compose the canvas-edit payload exactly as v1.
  // The outgoing payload for the composer's text and files, with the canvas
  // edits exported as they stand now. Null when the export failed: the
  // message is then parked in the prompt rather than sent without them.
  const buildOutgoingPayload = useCallback(async (text, files, { overwriteConfirmed = false } = {}) => {
    let canvasEdit = null;
    if (canvasHasEdits && workbookRef.current && canvasFile) {
      const { edits, hasEdits } = opAccumulatorRef.current.summarize();
      if (hasEdits && !overwriteConfirmed) {
        // A canvas edit replaces the whole working file. If that file has a
        // newer saved version than the one this workbook was opened from, and
        // the newer one is not this page's own earlier edit (which the open
        // workbook already contains), sending would overwrite someone else's
        // change: another tab's or user's edit, or a new copy from the agent.
        const changedElsewhere = workingFileChangedElsewhere(
          sessionRef.current.messages,
          canvasFile,
          sessionRef.current.isOwnSend,
        );
        if (changedElsewhere) {
          setStaleCanvasPrompt({ text, files, filename: canvasFile.filename || 'canvas_edit.xlsx' });
          return null;
        }
      }
      if (hasEdits) {
        let blob = null;
        try {
          blob = await exportWorkbookWithRetry(workbookRef);
        } catch (error) {
          // The composer promised these edits would go with this message, so
          // the message must not quietly go without them. The Composer has
          // already cleared itself: the payload is parked in the prompt, which
          // either sends it with a note the agent can act on, or hands it back.
          console.error('Error exporting canvas edits:', error);
          setExportFailurePrompt({
            text,
            files,
            filename: canvasFile.filename || 'canvas_edit.xlsx',
            count: edits.length,
          });
          return null;
        }
        // The exported workbook is one more upload, and the backend counts
        // it toward the combined limit the composer checked without it.
        const attachmentBytes = files.reduce((sum, file) => sum + file.size, 0);
        if (attachmentBytes + blob.size > MAX_TOTAL_UPLOAD_BYTES) {
          composerRef.current?.restore({ text, files });
          notify(
            'Your attachments and the canvas edits add up to more than the 40 MB limit per message — remove an attachment, or send the edits on their own.',
            { severity: 'warning' }
          );
          return null;
        }
        canvasEdit = {
          blob,
          filename: canvasFile.filename || 'canvas_edit.xlsx',
          // The version these edits were made on. Kept on this page only, for
          // checking a queued send again when it fires; it is not uploaded.
          openedFrom: {
            gcsPath: canvasFile.gcsPath,
            sandboxPath: canvasFile.sandboxPath,
            toolUseId: canvasFile.toolUseId,
            description: canvasFile.description,
          },
          metadata: {
            source_filename: canvasFile.filename,
            source_tool_use_id: canvasFile.toolUseId || null,
            edits,
          },
        };
      }
    }
    // The user's "send anyway" travels with the message, so that it is not
    // asked a second time if the message is then queued behind a running turn.
    return { text, files, canvasEdit, staleOverwriteConfirmed: overwriteConfirmed };
  }, [canvasHasEdits, canvasFile, notify]);

  const handleSend = useCallback(async (text, files) => {
    setSendInProgress(true);
    try {
      const payload = await buildOutgoingPayload(text, files);
      if (payload) await sessionRef.current.sendTurn(payload);
    } finally {
      setSendInProgress(false);
    }
  }, [buildOutgoingPayload]);

  // The stale-workbook prompt's three answers. The Composer cleared itself when
  // Send was pressed, so every answer either sends the parked message or hands
  // it back.
  const handleSendStaleCanvasAnyway = useCallback(async () => {
    const parked = staleCanvasPrompt;
    if (!parked) return;
    setStaleCanvasPrompt(null);
    setSendInProgress(true);
    try {
      // A message held back from the queue already carries its exported
      // workbook; anything else is built now from the open canvas.
      const payload = parked.queuedPayload
        ? { ...parked.queuedPayload, staleOverwriteConfirmed: true }
        : await buildOutgoingPayload(parked.text, parked.files, { overwriteConfirmed: true });
      if (payload) await sessionRef.current.sendTurn(payload);
    } finally {
      setSendInProgress(false);
    }
  }, [staleCanvasPrompt, buildOutgoingPayload]);

  const handleReloadLatestCanvas = useCallback(async () => {
    const parked = staleCanvasPrompt;
    if (!parked) return;
    setStaleCanvasPrompt(null);
    composerRef.current?.restore({ text: parked.text, files: parked.files });
    // The file to reload is the one the parked edits were made on. A message
    // held back from the queue recorded it, because the canvas may have moved
    // on to another workbook, or been closed, since Send was pressed.
    const editedFile = parked.queuedPayload?.canvasEdit?.openedFrom || canvasFile;
    if (!editedFile) {
      notify('Your message is back in the composer. The workbook is no longer open, so there was nothing to reload.', {
        severity: 'info',
      });
      return;
    }
    // Opening the file again loads its newest version and drops these edits.
    const reloadEditedFile = async () => {
      const opened = await openInCanvas({
        filename: parked.filename,
        description: editedFile.description,
        gcsPath: editedFile.gcsPath,
        sandboxPath: editedFile.sandboxPath,
        toolUseId: editedFile.toolUseId,
        contentType: contentTypeFor(parked.filename),
      });
      // Only a real failure is reported. An open that was overtaken (null)
      // means the canvas, or the chat, has already moved on.
      if (opened === false) {
        notify(`Could not reload the latest version of ${parked.filename}. Your message is back in the composer.`, {
          severity: 'warning',
        });
      }
    };
    // A message held back from the queue can outlive the workbook it was made
    // on: the turn it waited behind may have put another file, or a newer
    // version of this one, in the canvas, and the person may have edited that.
    // The prompt warned only about the parked edits, so edits to what the
    // canvas shows now are asked about before the reload replaces them.
    const canvasHasMovedOn = !!canvasFile && canvasFile.gcsPath !== editedFile.gcsPath;
    if (canvasHasMovedOn) {
      requestDiscardEdits(reloadEditedFile);
      return;
    }
    await reloadEditedFile();
  }, [staleCanvasPrompt, canvasFile, openInCanvas, notify, requestDiscardEdits]);

  const handleCancelStaleCanvasSend = useCallback(() => {
    const parked = staleCanvasPrompt;
    if (!parked) return;
    setStaleCanvasPrompt(null);
    composerRef.current?.restore({ text: parked.text, files: parked.files });
  }, [staleCanvasPrompt]);

  // Send the parked message without its edits. The note is the only channel
  // to the agent (user_message is the whole request), so it is part of the
  // message text — and reads as such in the bubble. The edits stay in the
  // canvas, still marked unsent, for a later attempt or for the user to
  // describe them when the agent asks. They last only until the agent puts a
  // file in the canvas, which replaces the workbook they were made on; the
  // dialog says so.
  const handleSendWithoutEdits = useCallback(async () => {
    const parked = exportFailurePrompt;
    if (!parked) return;
    setExportFailurePrompt(null);
    const note = `(Decode could not export my ${parked.count} canvas edit${parked.count === 1 ? '' : 's'} to "${parked.filename}" — they are not attached to this message.)`;
    const text = parked.text ? `${parked.text}\n\n${note}` : note;
    await sessionRef.current.sendTurn({ text, files: parked.files, canvasEdit: null });
  }, [exportFailurePrompt]);

  // Cancel: the message and files go back into the composer, edits untouched.
  const handleCancelSendWithoutEdits = useCallback(() => {
    const parked = exportFailurePrompt;
    if (!parked) return;
    setExportFailurePrompt(null);
    composerRef.current?.restore({ text: parked.text, files: parked.files });
  }, [exportFailurePrompt]);

  const isDraftState = !effectiveAssistantId && session.messages.length === 0;
  // The workbook is read-only whenever the agent holds the chat: a running turn
  // (own stream, an approval resume, the post-reply release), a send waiting to
  // fire (its workbook export was already taken when Send was pressed), or a
  // turn parked for approval (its resume streams straight back here). Every
  // edit-loss path in H50 — completion re-snapshotting an edit made after the
  // export, or a canvas_file replacing an unsent edit — needed an edit made
  // inside one of those windows, so closing them is the whole fix.
  const awaitingApproval = session.messages.some((message) => message.paused);
  // A file the user uploaded is read-only regardless of turn state: canvas
  // edits are resolved against the agent-generated file part they came from
  // (by tool_use_id), and an upload has none.
  let canvasLockReason = null;
  if (canvasFile && !canvasFile.toolUseId) {
    canvasLockReason = 'Files you uploaded open read-only.';
  } else if (sendInProgress) {
    canvasLockReason = 'Your edits are being sent to the Decode Agent.';
  } else if (session.turnActive || session.queuedSend) {
    canvasLockReason = 'The Decode Agent may be editing this file.';
  } else if (awaitingApproval) {
    canvasLockReason = "Please approve or deny the agent's last actions before editing.";
  }
  const canvasEditInfo = canvasHasEdits && canvasFile
    ? { filename: canvasFile.filename, count: canvasEditCount }
    : null;
  const filePaneVisible = !!canvasFile;
  const filePaneReopen = canvasFile && isMobile && filePaneCollapsed
    ? { filename: canvasFile.filename, onClick: () => setFilePaneCollapsed(false) }
    : null;

  // Keyed by chat: a draft typed in one chat is never carried into another, and
  // New chat starts empty. A draft's first send mints its id in the same tick the
  // composer clears itself, so that remount loses nothing.
  const composer = (
    <Composer
      key={`${effectiveAssistantId ?? 'draft'}|${sessionEpoch}`}
      ref={composerRef}
      onSend={handleSend}
      onStop={session.stopTurn}
      canStop={session.canStop}
      stopping={session.stopping}
      queued={session.queuedSend}
      canvasEditInfo={canvasEditInfo}
      contextPct={session.contextPct}
      notify={notify}
      dragContainerRef={centreColumnRef}
    />
  );

  return (
    <Box sx={{ display: 'flex', position: 'relative', height: 'calc(100vh - 64px)', overflow: 'hidden', bgcolor: 'background.default' }}>
      <ChatSidebar
        chatList={chatList}
        companyName={companyName}
        activeChatId={effectiveAssistantId}
        activeChatName={session.chatName}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        collapsed={sidebarCollapsed}
        onCollapsedChange={handleSidebarCollapsedChange}
        isMobile={isMobile}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        onDeleteChat={handleDeleteChat}
      />

      {/* Centre column — also the drag-and-drop target (§8.8). */}
      <Box
        ref={centreColumnRef}
        sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}
      >
        <ChatTopBar
          chatName={session.chatName}
          hasChat={!!effectiveAssistantId && session.chatExists}
          turnActive={session.turnActive}
          streaming={session.streamState === 'streaming'}
          contextPct={session.contextPct}
          isMobile={isMobile}
          sidebarCollapsed={sidebarCollapsed}
          onOpenSidebar={handleOpenSidebar}
          onRename={handleRenameChat}
          filePaneReopen={filePaneReopen}
        />

        {session.streamState === 'following' && (
          <Box
            sx={{
              px: 2,
              py: 0.75,
              display: 'flex',
              justifyContent: 'center',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(240, 178, 90, 0.12)' : 'rgba(232, 158, 60, 0.12)',
            }}
          >
            {/* Following covers a dropped connection, a viewer the backend cut
                loose for falling behind, and a turn that lost its lease, so the
                wording names what the page is doing rather than one cause. */}
            <Typography variant="caption" color="text.secondary">
              Live streaming stopped — following the agent's saved progress.
            </Typography>
          </Box>
        )}

        {session.loadError && (
          <LoadFailure
            thing="this chat"
            onRetry={session.retryListeners}
            align="left"
            sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
          />
        )}

        {isDraftState ? (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', pb: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3, px: 2 }}>
              <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>Decode Agent</Typography>
              <Typography variant="body1" color="text.secondary">What can I help you with?</Typography>
            </Box>
            {composer}
          </Box>
        ) : (
          <>
            <ChatThread
              key={effectiveAssistantId}
              messages={session.messages}
              queuedPreview={session.queuedPreview}
              onOpenFile={handleShowInCanvas}
              hasOlder={session.hasOlderMessages}
              loadingOlder={session.loadingOlder}
              onLoadOlder={session.loadOlderMessages}
            />
            {composer}
          </>
        )}

      </Box>

      {/* One permanent FilePane instance: a desktop layout column or a mobile
          overlay. Collapsing changes its width but never destroys the editor. */}
      {filePaneVisible && (
        <FilePane
          file={canvasFile}
          mobile={isMobile}
          collapsed={filePaneCollapsed}
          onToggleCollapse={() => setFilePaneCollapsed((prev) => !prev)}
          onMobileBack={() => setFilePaneCollapsed(true)}
          onClose={() => requestDiscardEdits(clearCanvasState)}
          onOp={handleCanvasOp}
          onLoad={handleCanvasLoad}
          workbookRef={workbookRef}
          hasEdits={canvasHasEdits}
          exportLive={canvasHasEdits || canvasEverEdited}
          lockReason={canvasLockReason}
          closeBlockedReason={canvasEditsBeingSent ? CANVAS_EDITS_BEING_SENT : null}
        />
      )}

      {/* Canvas edits that would not export: send without them, or take the
          message back. */}
      <Dialog open={!!exportFailurePrompt} onClose={handleCancelSendWithoutEdits}>
        <DialogTitle>Canvas edits could not be exported</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {exportFailurePrompt
              ? `Your ${exportFailurePrompt.count} unsent edit${exportFailurePrompt.count === 1 ? '' : 's'} to ${exportFailurePrompt.filename} could not be exported, so they cannot go with this message.`
              : ''}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You can send the message without them — the Decode Agent will be told the edits are missing, so you can describe them instead — or cancel and keep the message in the composer. Your edits stay in the canvas either way, until the Decode Agent next puts a file in the canvas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSendWithoutEdits}>Cancel</Button>
          <Button variant="contained" onClick={handleSendWithoutEdits}>Send without edits</Button>
        </DialogActions>
      </Dialog>

      {/* Canvas edits made on a workbook that has since changed elsewhere. */}
      <Dialog open={!!staleCanvasPrompt} onClose={handleCancelStaleCanvasSend}>
        <DialogTitle>This workbook has changed</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {staleCanvasPrompt
              ? `${staleCanvasPrompt.filename} was changed in this chat after you opened it, by edits sent from another tab or user, or by a new copy from the Decode Agent.`
              : ''}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Your edits replace the whole workbook, so sending them now will overwrite that change. You can reload the latest version, which discards your edits here, or send anyway. Your message is kept either way.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelStaleCanvasSend}>Cancel</Button>
          <Button onClick={handleReloadLatestCanvas}>Reload latest</Button>
          <Button color="error" onClick={handleSendStaleCanvasAnyway}>Send anyway</Button>
        </DialogActions>
      </Dialog>

      {/* Unsent work discard prompt: canvas edits (carried from v1) and/or a
          queued message. */}
      <Dialog open={!!discardPrompt} onClose={downloadingEdits ? undefined : () => setDiscardPrompt(null)}>
        <DialogTitle>
          {canvasHasEdits && discardPrompt?.queuedSend
            ? 'Unsent canvas edits and queued message'
            : canvasHasEdits ? 'Unsent canvas edits' : 'Queued message'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {unsentWorkWarning(canvasHasEdits, discardPrompt?.queuedSend)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardPrompt(null)} disabled={downloadingEdits}>Cancel</Button>
          {canvasHasEdits && (
            <Button onClick={handleDownloadAndDiscardEdits} disabled={downloadingEdits}>
              {downloadingEdits ? 'Exporting…' : 'Download & Discard'}
            </Button>
          )}
          <Button color="error" onClick={handleDiscardEdits} disabled={downloadingEdits}>Discard</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={8000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar?.severity || 'info'}
          onClose={() => setSnackbar(null)}
          action={(snackbar?.onRetry || snackbar?.retryable) ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const { onRetry } = snackbar;
                setSnackbar(null);
                if (onRetry) onRetry();
                // Sends what the composer and the canvas show now, not the
                // payload as it was when the send failed (M225). The draft is
                // taken only once the session has a failed send to retry, so
                // a Retry left over from an earlier failure cannot swallow a
                // newer draft.
                else {
                  setSendInProgress(true);
                  sessionRef.current.retryLastSend(async () => {
                    const draft = composerRef.current?.takeDraft();
                    return draft ? buildOutgoingPayload(draft.text, draft.files) : null;
                  }).finally(() => setSendInProgress(false));
                }
              }}
            >
              Retry
            </Button>
          ) : undefined}
          sx={{ alignItems: 'center' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Agent;
